import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { toast } from "sonner";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ApercuEmail } from "@/components/ApercuEmail";
import { UserPlus, ArrowLeft, Save, Send, CheckCircle2, XCircle } from "lucide-react";

const VARIABLES = [
  ["{{civilite}}", "Civilité"], ["{{prenom}}", "Prénom"], ["{{nom}}", "Nom"],
  ["{{lien}}", "Lien de connexion"],
];
const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light";

export default function OnboardingAdmin() {
  const [candidats, setCandidats] = useState(null);
  const [selection, setSelection] = useState(new Set());
  const [modele, setModele] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [signatures, setSignatures] = useState([]);
  const [sigId, setSigId] = useState("defaut");
  const [seminaires, setSeminaires] = useState([]);
  const [fEvent, setFEvent] = useState("");
  const [fType, setFType] = useState("");
  const [fStatut, setFStatut] = useState("");

  const loadCandidats = () => {
    const qs = new URLSearchParams();
    if (fEvent) qs.set("seminar_id", fEvent);
    if (fType) qs.set("type", fType);
    if (fStatut) qs.set("statut", fStatut);
    api(`/admin/onboarding/candidats${qs.toString() ? `?${qs.toString()}` : ""}`).then((d) => {
      setCandidats(d.candidats);
      setSelection(new Set(d.candidats.map((c) => c.id)));
    }).catch((e) => toast.error(e.message));
  };
  useEffect(() => {
    api("/admin/onboarding/modele").then(setModele).catch((e) => toast.error(e.message));
    api("/admin/messagerie/signatures").then((d) => setSignatures(d.items)).catch(() => {});
    api("/admin/seminaires").then((r) => setSeminaires(Array.isArray(r) ? r : (r.items || []))).catch(() => {});
  }, []);
  useEffect(() => { loadCandidats(); /* eslint-disable-next-line */ }, [fEvent, fType, fStatut]);

  const toggle = (id) =>
    setSelection((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const saveModele = async () => {
    setBusy(true);
    try {
      await api("/admin/onboarding/modele", { method: "PUT", body: modele });
      toast.success("Modèle d'onboarding enregistré");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const envoyer = async () => {
    if (!selection.size) return;
    if (!window.confirm(
      `Envoyer l'email d'onboarding à ${selection.size} membre(s) ?\n\nChacun recevra un lien de connexion personnel (sans mot de passe), valable 7 jours.`)) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api("/admin/onboarding/envoyer", { method: "POST", body: { person_ids: [...selection], signature_id: sigId === "defaut" ? null : sigId } });
      setResult(r);
      if (r.erreurs === 0) toast.success(`${r.envoyes} email(s) d'onboarding envoyé(s)`);
      else toast.error(`${r.erreurs} erreur(s) sur ${r.total}`);
      loadCandidats();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppLayout variant="admin">
      <div data-testid="onboarding-page" className="max-w-4xl space-y-6 animate-fade-in">
        <div>
          <Link to="/admin/messagerie" data-testid="onboarding-retour"
            className="inline-flex items-center gap-2 text-sm text-cfhtb-text hover:text-cfhtb-primary transition-colors">
            <ArrowLeft className="h-4 w-4" /> Retour à la messagerie
          </Link>
          <div className="flex items-center gap-3 mt-2">
            <UserPlus className="h-7 w-7 text-cfhtb-primary" />
            <div>
              <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Onboarding</h1>
              <p className="text-sm text-cfhtb-text">
                Email de bienvenue envoyé manuellement aux membres qui ne se sont jamais connectés.
                Chaque email contient un lien de connexion personnel valable 7 jours.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-4" data-testid="onboarding-modele">
          <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Modèle d'email</h2>
          {!modele ? (
            <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-cfhtb-heading mb-1.5">Objet de l'email *</label>
                <input data-testid="onboarding-sujet" className={inputCls} value={modele.sujet}
                  onChange={(e) => setModele({ ...modele, sujet: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-cfhtb-heading mb-1.5">
                  Contenu <span className="font-normal text-xs text-cfhtb-text">(la variable {"{{lien}}"} est remplacée par le lien personnel de chaque membre à l'envoi)</span>
                </label>
                <RichTextEditor testid="onboarding-wysiwyg" variables={VARIABLES} minHeight={220}
                  content={modele.contenu}
                  onUpdate={(html) => setModele((m) => ({ ...m, contenu: html }))} />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-[220px]">
                  <label className="block text-xs font-medium text-cfhtb-heading mb-1">Signature de l'email</label>
                  <select data-testid="onboarding-signature-select" value={sigId} onChange={(e) => setSigId(e.target.value)} className={inputCls}>
                    {signatures.length === 0 && <option value="defaut">Signature par défaut</option>}
                    {signatures.map((s) => (
                      <option key={s.id} value={s.id}>{s.nom}{s.defaut ? " (par défaut)" : ""}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-3">
                  <ApercuEmail contenu={modele.contenu} mode="onboarding" testid="onboarding-apercu" signatureId={sigId === "defaut" ? null : sigId} />
                  <button data-testid="onboarding-save" onClick={saveModele} disabled={busy}
                    className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg transition-colors disabled:opacity-50">
                    <Save className="h-4 w-4" /> Enregistrer le modèle
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-4" data-testid="onboarding-destinataires">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">
              Membres jamais connectés {candidats && <span className="text-cfhtb-primary" data-testid="onboarding-total">({selection.size}/{candidats.length} sélectionnés)</span>}
            </h2>
            {candidats && candidats.length > 0 && (
              <div className="flex gap-2 text-xs">
                <button data-testid="onboarding-select-all" onClick={() => setSelection(new Set(candidats.map((c) => c.id)))}
                  className="text-cfhtb-primary hover:underline font-medium">Tout sélectionner</button>
                <span className="text-slate-300">|</span>
                <button data-testid="onboarding-select-none" onClick={() => setSelection(new Set())}
                  className="text-cfhtb-text hover:underline">Tout désélectionner</button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 pb-4" data-testid="onboarding-filtres">
            <div className="min-w-[240px] flex-1">
              <label className="block text-xs font-medium text-cfhtb-heading mb-1">Restreindre à un événement</label>
              <select data-testid="onboarding-filtre-event" className={inputCls} value={fEvent} onChange={(e) => setFEvent(e.target.value)}>
                <option value="">Tous les événements</option>
                {seminaires.map((s) => <option key={s.id} value={s.id}>{s.titre}</option>)}
              </select>
            </div>
            <div className="min-w-[150px]">
              <label className="block text-xs font-medium text-cfhtb-heading mb-1">Type</label>
              <select data-testid="onboarding-filtre-type" className={inputCls} value={fType} onChange={(e) => setFType(e.target.value)}>
                <option value="">Tous</option>
                <option value="PRATICIEN">Praticiens</option>
                <option value="VISITEUR">Visiteurs</option>
              </select>
            </div>
            <div className="min-w-[150px]">
              <label className="block text-xs font-medium text-cfhtb-heading mb-1">Statut</label>
              <select data-testid="onboarding-filtre-statut" className={inputCls} value={fStatut} onChange={(e) => setFStatut(e.target.value)}>
                <option value="">Actifs + Prospects</option>
                <option value="ACTIF">Actifs</option>
                <option value="PROSPECT">Prospects</option>
              </select>
            </div>
            {(fEvent || fType || fStatut) && (
              <button type="button" data-testid="onboarding-filtre-reset"
                onClick={() => { setFEvent(""); setFType(""); setFStatut(""); }}
                className="text-xs text-cfhtb-text hover:text-cfhtb-primary underline pb-2">Réinitialiser</button>
            )}
          </div>
          {!candidats ? (
            <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
          ) : candidats.length === 0 ? (
            <p className="text-sm text-cfhtb-text bg-cfhtb-bg/50 rounded-md px-4 py-3" data-testid="onboarding-vide">
              Aucun membre en attente de première connexion — tout le monde a déjà ouvert l'extranet.
            </p>
          ) : (
            <ul className="max-h-72 overflow-y-auto divide-y divide-slate-50 border border-slate-100 rounded-md" data-testid="onboarding-list">
              {candidats.map((c) => (
                <li key={c.id} className="px-3 py-1.5">
                  <label className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`onboarding-candidat-${c.id}`}>
                    <input type="checkbox" checked={selection.has(c.id)} onChange={() => toggle(c.id)}
                      className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary focus:ring-cfhtb-light" />
                    <span className="text-cfhtb-heading">{c.civilite ? `${c.civilite} ` : ""}{c.prenom} {c.nom}</span>
                    <span className="text-cfhtb-text/70">({c.email})</span>
                    <span className="text-xs text-cfhtb-text/60 ml-auto">{c.type} · {c.statut}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-4 pt-1">
            <button data-testid="onboarding-send-button" onClick={envoyer} disabled={busy || !selection.size}
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-5 py-2.5 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-50">
              <Send className="h-4 w-4" />
              {busy ? "Envoi en cours…" : `Envoyer l'onboarding (${selection.size})`}
            </button>
            <p className="text-xs text-cfhtb-text/70">L'envoi est immédiat et passe par Brevo, avec la signature CFHTB.</p>
          </div>
          {result && (
            <div data-testid="onboarding-result"
              className={`flex items-start gap-3 rounded-md border p-4 text-sm ${
                result.erreurs === 0
                  ? "bg-cfhtb-success/5 border-cfhtb-success/30 text-cfhtb-success"
                  : "bg-cfhtb-error/5 border-cfhtb-error/30 text-cfhtb-error"}`}>
              {result.erreurs === 0 ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <XCircle className="h-5 w-5 shrink-0" />}
              <p className="font-medium">{result.envoyes} envoyé(s), {result.erreurs} erreur(s) sur {result.total} destinataire(s).</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
